/**
 * @fileoverview A customizable Modal component with header, body, and footer subcomponents.
 * @module Modal
 */

// React
import React, { useRef, forwardRef } from 'react'
import PropTypes from 'prop-types'

// Libraries
import { Transition, config } from 'react-transition-group'

// Hooks
import { useLockScroll } from 'shared/utils/useLockScroll'

// Components
import PortalWrapper from 'ui/components/PortalWrapper'
import Icon from 'ui/components/Icon'
import H3 from 'ui/elements/H3'
import { Backdrop, Body, Container, Header, Footer, Close } from 'store/blocks/Modal'

/**
 * @typedef {Object} ModalModifiers
 * @property {string} [backdrop] - Custom CSS class for the backdrop.
 * @property {string} [container] - Custom CSS class for the container.
 */

/**
 * A customizable Modal component that can be opened and closed.
 * It uses react-transition-group for enter/exit animations
 * and custom hooks for handling outside clicks and locking the body scroll.
 *
 * @component
 * @param {Object} props - The properties that define the Modal's behavior and content.
 * @param {boolean} props.open - Determines whether the modal is visible.
 * @param {Function} [props.onClose] - Callback function to be called when the modal should be closed.
 * @param {ModalModifiers} [props.modifiers] - Custom CSS classes for styling different parts of the modal.
 * @param {React.ReactNode} props.children - The content to be rendered inside the modal.
 *
 * @example
 * // Named import
 * import { Modal, ModalHeader, ModalBody, ModalFooter } from 'ui/components/Modal'
 *
 * <Modal
 *   open={isOpen}
 *   onClose={handleClose}
 *   modifiers={{ backdrop: 'custom-backdrop', container: 'custom-container' }}
 *  >
 *   <ModalHeader>Modal Title</ModalHeader>
 *   <ModalBody>Modal Content</ModalBody>
 *   <ModalFooter>
 *     <button onClick={handleClose}>Close</button>
 *   </ModalFooter>
 * </Modal>
 *
 * @example
 * // Namespace import
 * import * as Modal from 'ui/components/Modal'
 *
 * <Modal.Root
 *   open={isOpen}
 *   onClose={handleClose}
 *   modifiers={{ backdrop: 'custom-backdrop', container: 'custom-container' }}
 *  >
 *   <Modal.Header>Modal Title</Modal.Header>
 *   <Modal.Body>Modal Content</Modal.Body>
 *   <Modal.Footer>
 *     <button onClick={handleClose}>Close</button>
 *   </Modal.Footer>
 * </Modal.Root>
 */
export const Modal = ({ open, onClose, modifiers, children }) => {
  // disables animations when in test mode
  config.disabled = !!window.testMode

  const nodeRef = useRef(null)

  useLockScroll(open)

  const handleBackdropClick = (event) => {
    if (event.target === event.currentTarget) {
      onClose?.()
    }
  }

  return (
    <Transition nodeRef={nodeRef} in={open} timeout={{ enter: 0, exit: 250 }}>
      {(state) => (
        <PortalWrapper open={open || state === 'exiting' || state === 'entered'}>
          <Backdrop onClick={handleBackdropClick} state={state} modifiers={modifiers?.backdrop}>
            <Container data-tid="Modal" state={state} modifiers={modifiers?.container}>
              {children}
            </Container>
          </Backdrop>
        </PortalWrapper>
      )}
    </Transition>
  )
}

Modal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func,
  modifiers: PropTypes.shape({
    backdrop: PropTypes.string,
    container: PropTypes.string
  }),
  children: PropTypes.node
}

/**
 * The close button for the Modal.
 *
 * @component
 * @param {Object} props - The properties for the ModalClose.
 * @param {Function} props.onClose - Callback function to be called when the close button is clicked.
 *
 * @example
 * // Named import
 * import { ModalClose } from 'ui/components/Modal'
 *
 * <ModalClose onClose={handleClose} />
 *
 * @example
 * // Namespace import
 * import * as Modal from 'ui/components/Modal'
 *
 * <Modal.Close onClose={handleClose} />
 */
export const ModalClose = forwardRef(({ onClose }, ref) => (
  <Close ref={ref} onClick={onClose} data-tid="Close icon">
    <Icon icon="times" />
  </Close>
))

ModalClose.displayName = 'ModalClose'

ModalClose.propTypes = {
  onClose: PropTypes.func
}

/**
 * The header section of the Modal.
 *
 * @component
 * @param {Object} props - The properties for the ModalHeader.
 * @param {React.ReactNode} props.children - The content to be rendered inside the header.
 * @param {string} [props.modifiers] - Custom CSS class for styling the header.
 *
 * @example
 * // Named import
 * import { ModalHeader } from 'ui/components/Modal'
 *
 * <ModalHeader modifiers="custom-header-class">Modal Title</ModalHeader>
 *
 * @example
 * // Namespace import
 * import * as Modal from 'ui/components/Modal'
 *
 * <Modal.Header modifiers="custom-header-class">Modal Title</Modal.Header>
 */
export const ModalHeader = forwardRef(({ children, ...props }, ref) => (
  <Header ref={ref} {...props}>
    {children}
  </Header>
))

ModalHeader.displayName = 'ModalHeader'

ModalHeader.propTypes = {
  children: PropTypes.node
}

/**
 * The title component for the Modal header.
 *
 * @component
 * @param {Object} props - The properties for the ModalTitle.
 * @param {React.ReactNode} props.children - The content to be rendered as the modal title.
 *
 * @example
 * // Named import
 * import { ModalTitle } from 'ui/components/Modal'
 *
 * <ModalTitle>Welcome to Our Application</ModalTitle>
 *
 * @example
 * // Namespace import
 * import * as Modal from 'ui/components/Modal'
 *
 * <Modal.Title>Welcome to Our Application</Modal.Title>
 */
export const ModalTitle = ({ children, ...props }) => <H3 {...props}>{children}</H3>

ModalTitle.propTypes = {
  children: PropTypes.node
}

/**
 * The body section of the Modal.
 *
 * @component
 * @param {Object} props - The properties for the ModalBody.
 * @param {React.ReactNode} props.children - The content to be rendered inside the body.
 * @param {string} [props.modifiers] - Custom CSS class for styling the body.
 *
 * @example
 * // Named import
 * import { ModalBody } from 'ui/components/Modal'
 *
 * <ModalBody modifiers="custom-body-class">
 *   <p>This is the main content of the modal.</p>
 * </ModalBody>
 *
 * @example
 * // Namespace import
 * import * as Modal from 'ui/components/Modal'
 *
 * <Modal.Body modifiers="custom-body-class">
 *   <p>This is the main content of the modal.</p>
 * </Modal.Body>
 */
export const ModalBody = forwardRef(({ children, ...props }, ref) => (
  <Body ref={ref} {...props}>
    {children}
  </Body>
))

ModalBody.displayName = 'ModalBody'

ModalBody.propTypes = {
  children: PropTypes.node
}

/**
 * The footer section of the Modal.
 *
 * @component
 * @param {Object} props - The properties for the ModalFooter.
 * @param {React.ReactNode} props.children - The content to be rendered inside the footer.
 * @param {string} [props.modifiers] - Custom CSS class for styling the footer.
 *
 * @example
 * // Named import
 * import { ModalFooter } from 'ui/components/Modal'
 *
 * <ModalFooter modifiers="custom-footer-class">
 *   <button onClick={handleClose}>Close</button>
 *   <button onClick={handleSave}>Save</button>
 * </ModalFooter>
 *
 * @example
 * // Namespace import
 * import * as Modal from 'ui/components/Modal'
 *
 * <Modal.Footer modifiers="custom-footer-class">
 *   <button onClick={handleClose}>Close</button>
 *   <button onClick={handleSave}>Save</button>
 * </Modal.Footer>
 */
export const ModalFooter = forwardRef(({ children, ...props }, ref) => (
  <Footer ref={ref} data-tid="Modal actions" {...props}>
    {children}
  </Footer>
))

ModalFooter.displayName = 'ModalFooter'

ModalFooter.propTypes = {
  children: PropTypes.node
}
